import { Icon20ChevronRight } from '@vkontakte/icons';
import { Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserStorageActions, UserStorageSelectors } from 'reactApp/modules/user/userStorage';
import type { RootState } from 'reactApp/store';
import type { Props } from 'reactApp/ui/Datalist/DataList.types';
import { noop } from 'reactApp/utils/helpers';
import opener from 'reactApp/utils/opener';

import styles from './LastFilesWidget.css';

export const LAST_FILES_WIDGET_LOCAL_STORAGE_KEY = 'lastfilescollapsed';
export interface LastFilesWidgetProps {
    lastFiles: any[];
    onExpand?: (expanded: boolean) => void;
    DataList: React.ComponentType<Props>;
}

export const LastFilesWidget = memo(function LastFilesWidget({ lastFiles, onExpand = noop, DataList }: LastFilesWidgetProps) {
    const dispatch = useDispatch();

    const localstoreIsClosed = useSelector((state: RootState) =>
        Boolean(UserStorageSelectors.get(state, LAST_FILES_WIDGET_LOCAL_STORAGE_KEY))
    );
    const [isClosed, setClosed] = useState<boolean>(localstoreIsClosed);

    const titleRef = useRef<HTMLDivElement | null>(null);

    const handleExpand = useCallback(() => {
        const newValue = !isClosed;
        dispatch(UserStorageActions.set(LAST_FILES_WIDGET_LOCAL_STORAGE_KEY, newValue));
        setClosed(newValue);
    }, [isClosed, dispatch, setClosed]);

    useEffect(() => {
        onExpand(!localstoreIsClosed);
    }, [localstoreIsClosed, onExpand]);

    const handleFeedSeeAll = useCallback(() => {
        opener('/feed', true);
    }, []);

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div className={styles.collapser} onClick={handleExpand}>
                    <Icon20ChevronRight
                        className={classNames(styles.expandIcon, {
                            [styles.expandIconExpanded]: !localstoreIsClosed,
                        })}
                    />
                    <div ref={titleRef}>
                        <Title className={styles.title} level="2">
                            Последние файлы
                        </Title>
                    </div>
                </div>
                <div className={styles.headerContent}>
                    {!localstoreIsClosed && (
                        <div className={styles.leftControls}>
                            <div className={styles.feedAll} onClick={handleFeedSeeAll}>
                                Посмотреть все
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className={classNames(styles.body, { [styles.bodyClosed]: localstoreIsClosed })}>
                <div className={styles.lastFiles}>
                    <DataList
                        goTo={noop}
                        storage={EStorageType.feed}
                        viewMode={EViewMode.thumbs}
                        gaSuffix="wjfeed"
                        disableCenteredLoader
                        limitItemsNumber={10}
                        disableLoadOnScroll
                        disableTips
                        itemsList={lastFiles?.map((item) => item.id)}
                        storiesWidget
                        selectBoxEnabled={false}
                    />
                </div>
            </div>
        </div>
    );
});
